import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Modal } from '@rtkwlf/ui';
import { sessionSelector } from '../../../reducers/session';
import { VirtualLogCollector } from '../../../types';
import { useTrashablePromise } from '../../../hooks';
import { apiRawFetch, apiRequest } from '../../../APIClient';
import { getProduct, VIRTUAL_SENSOR_TYPES } from './VanillaSensorListView';

export const useRegisterSensor = (
  getVirtualLogCollectors: () => void,
  sensors: any,
) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [vlcToRegister, setVlcToRegister] =
    React.useState<VirtualLogCollector | null>(null);

  const closeModal = React.useCallback(() => {
    setVlcToRegister(null);
    setCustomerNameText('');
  }, []);

  const [customerNameText, setCustomerNameText] = React.useState('');
  const onCustomerNameTextChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      setCustomerNameText(event.currentTarget.value);
    },
    [],
  );
  const onConfirmRegister = React.useCallback((vlc: VirtualLogCollector) => {
    setVlcToRegister(vlc);
  }, []);

  const { registerPromise } = useTrashablePromise();
  const dispatch = useDispatch();
  const customerName = useSelector((store) =>
    sessionSelector.getCustomerName(store),
  );
  const isMSP = useSelector((store) => sessionSelector.isMSP(store));
  const isAwnUser = useSelector((store) => sessionSelector.isAwnUser(store));

  const onRegister = React.useCallback(async () => {
    if (!vlcToRegister) {
      return;
    }

    try {
      setIsLoading(true);
      await registerPromise(apiRawFetch.registerLogCollector(vlcToRegister));
      getVirtualLogCollectors();
      dispatch(
        apiRequest.setGlobalStatus(
          `${
            vlcToRegister.is_vanilla
              ? 'Physical appliance'
              : 'Virtual appliance'
          } ${vlcToRegister.serial_number} successfully activated.`,
          'success',
        ),
      );
    } catch (err) {
      let productType = getProduct(vlcToRegister);
      if (vlcToRegister.is_vanilla) {
        productType = 'vanilla';
      } else if (!(productType.toLowerCase() in VIRTUAL_SENSOR_TYPES)) {
        productType = '';
      }
      if (
        vlcToRegister.deployment_type !== 'scanner' && // scanners are free and unlimited
        vlcToRegister.deployment_type !== 'vlc' && // vlc are free and unlimited
        productType !== '' &&
        sensors[productType.toLowerCase()] &&
        sensors[productType.toLowerCase()].quota ===
          sensors[productType.toLowerCase()].used
      ) {
        dispatch(
          apiRequest.setGlobalStatus(
            `${
              // @ts-ignore
              productType === 'vanilla' ? 'Physical Appliance' : productType
            } license count exceeded. Could not activate
            ${
              vlcToRegister.is_vanilla
                ? ' physical appliance'
                : ' virtual appliance'
            } ${vlcToRegister.serial_number}.`,
            'danger',
          ),
        );
      } else {
        dispatch(
          apiRequest.setGlobalStatus(err.message, err.color || 'danger'),
        );
      }
    } finally {
      setVlcToRegister(null);
      setIsLoading(false);
      setCustomerNameText('');
    }
  }, [
    vlcToRegister,
    dispatch,
    registerPromise,
    getVirtualLogCollectors,
    sensors,
  ]);

  const modal: React.ReactNode = React.useMemo(() => {
    if (!vlcToRegister) {
      return null;
    }
    const shouldConfirmClient = isAwnUser || isMSP;
    const isCtaDisabled =
      isLoading ||
      (shouldConfirmClient &&
        customerNameText.toLowerCase() !== customerName?.toLowerCase());

    const type =
      vlcToRegister?.product_type?.toLowerCase() === 'vsensor'
        ? 'virtual sensor'
        : vlcToRegister?.product_type?.toLowerCase() === 'vlc'
        ? 'virtual log collector'
        : vlcToRegister?.is_vanilla
        ? 'physical appliance'
        : 'virtual appliance';

    let message = `Activating the ${type} ${vlcToRegister.serial_number} will enable it to forward security relevant logs to the Arctic Wolf Platform.`;

    if (shouldConfirmClient) {
      message = `You are activating the ${type} ${vlcToRegister.short_name} ${vlcToRegister.serial_number} for ${customerName}.
            Please ensure this is the customer where this ${type} is installed.`;
    }

    return (
      <Modal
        visible
        title={
          <span>
            Activate {vlcToRegister.is_vanilla ? 'Physical' : 'Virtual'}{' '}
            Appliance
          </span>
        }
        onCancel={closeModal}
        onOk={onRegister}
        footer={
          <>
            <Button
              data-cy="cancel-register"
              type="primary"
              ghost
              disabled={isLoading}
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              data-cy="confirm-register"
              onClick={onRegister}
              disabled={isCtaDisabled}
            >
              <span>
                Activate {vlcToRegister.is_vanilla ? 'Physical' : 'Virtual'}{' '}
                Appliance
              </span>
            </Button>
          </>
        }
      >
        <div style={{ maxWidth: 600 }}>
          <p>{message}</p>
          {shouldConfirmClient && (
            <>
              <p>
                To confirm activation, enter the name of the customer in the
                text input field.
              </p>
              <Input
                placeholder="Customer name"
                value={customerNameText}
                onChange={onCustomerNameTextChange}
              />
            </>
          )}
        </div>
      </Modal>
    );
  }, [
    vlcToRegister,
    closeModal,
    customerName,
    onRegister,
    isAwnUser,
    isLoading,
    isMSP,
    customerNameText,
  ]);

  return {
    modal,
    onConfirmRegister,
  };
};
